/**
 * OAuthClientDetail (Phase 2.A.2).
 *
 * Drawer for one OAuth client. Same shape as WorkgroupDetail —
 * detail/create modes, inline edit, server-paged list at the parent.
 *
 * Shows + edits:
 *   - id (auto ULID on create; immutable)
 *   - type (claude_desktop / claude_ai / s2s / dev)
 *   - display_name
 *   - owning WG
 *   - allowed_scopes (chip list)
 *   - redirect_uris (chip list)
 *   - allowed_virtual_server_slugs (chip list)
 *   - status (active / retired)
 *
 * Designed for many redirect URIs / scopes — chip lists with type-to-add.
 */
import { useEffect, useState, type JSX } from 'react';
import { AlertTriangle, Check, Edit2, KeyRound, Trash2, X, Zap } from 'lucide-react';

import {
  ApiError,
  useClient,
  useTestClient,
  useCreateClient,
  useDeleteClient,
  useUpdateClient,
  useWorkgroups,
} from '@/api/index.js';
import { Drawer } from '../Drawer.js';
import { HelpCallout } from '../HelpCallout.js';
import { Required, RequiredLegend } from '../Required.js';

interface OAuthClientDetailProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: 'detail' | 'create';
  readonly clientId?: string;
  readonly onPersisted?: (id: string) => void;
}

const CLIENT_TYPES = ['claude_desktop', 'claude_ai', 's2s', 'dev'] as const;
type ClientType = (typeof CLIENT_TYPES)[number];
const STATUSES = ['active', 'retired'] as const;
const SCOPE_SUGGESTIONS = ['mcp:discover', 'mcp:invoke', 'mcp:admin', 'mcp:audit-read'];

export function OAuthClientDetail({
  open,
  onClose,
  mode,
  clientId,
  onPersisted,
}: OAuthClientDetailProps): JSX.Element {
  const client = useClient(mode === 'detail' ? clientId : undefined);
  const wgs = useWorkgroups({ limit: 500 });

  const create = useCreateClient();
  const update = useUpdateClient();
  const remove = useDeleteClient();
  const test = useTestClient();

  const [editing, setEditing] = useState(mode === 'create');
  const [type, setType] = useState<ClientType>('claude_desktop');
  const [name, setName] = useState('');
  const [ownerWg, setOwnerWg] = useState('');
  const [scopes, setScopes] = useState<string[]>(['mcp:discover', 'mcp:invoke']);
  const [redirects, setRedirects] = useState<string[]>([]);
  const [vsSlugs, setVsSlugs] = useState<string[]>([]);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('active');
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'detail' && client.data) {
      setType(client.data.type as ClientType);
      setName(client.data.displayName);
      setOwnerWg(client.data.ownerWgId);
      setScopes([...client.data.allowedScopes]);
      setRedirects([...client.data.redirectUris]);
      setVsSlugs([...client.data.allowedVirtualServerSlugs]);
      setStatus(client.data.status as 'active' | 'retired');
      setEditing(false);
    } else if (mode === 'create' && open) {
      setType('claude_desktop');
      setName('');
      setOwnerWg(wgs.data?.items[0]?.id ?? '');
      setScopes(['mcp:discover', 'mcp:invoke']);
      setRedirects([]);
      setVsSlugs([]);
      setStatus('active');
      setEditing(true);
    }
  }, [mode, client.data, open, wgs.data]);

  function handleResult<T>(p: Promise<T>, after?: (v: T) => void): void {
    setActionError(null);
    p.then((v) => after?.(v)).catch((err: unknown) => {
      setActionError(
        err instanceof ApiError
          ? `${err.status}: ${JSON.stringify(err.body)}`
          : (err as Error).message,
      );
    });
  }

  function onSave(): void {
    setActionError(null);
    if (mode === 'create') {
      if (!name || !ownerWg) {
        setActionError('display_name + owning WG are required');
        return;
      }
      handleResult(
        create.mutateAsync({
          type,
          display_name: name,
          owner_wg_id: ownerWg,
          allowed_scopes: scopes,
          redirect_uris: redirects,
          allowed_virtual_server_slugs: vsSlugs,
          status,
        }),
        (v) => {
          onPersisted?.(v.id);
          onClose();
        },
      );
      return;
    }
    if (!client.data) return;
    handleResult(
      update.mutateAsync({
        id: client.data.id,
        patch: {
          display_name: name,
          allowed_scopes: scopes,
          redirect_uris: redirects,
          allowed_virtual_server_slugs: vsSlugs,
          status,
        },
      }),
      (v) => {
        setEditing(false);
        onPersisted?.(v.id);
      },
    );
  }

  function onDelete(): void {
    if (!client.data) return;
    if (!window.confirm(`Delete OAuth client "${client.data.displayName}"? This is permanent.`))
      return;
    handleResult(remove.mutateAsync(client.data.id), () => onClose());
  }

  const titleEl =
    mode === 'create' ? 'New OAuth client' : (client.data?.displayName ?? clientId ?? '...');
  const persisting = create.isPending || update.isPending || remove.isPending;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="OAuth client"
      title={titleEl}
      footer={
        editing ? (
          <>
            <button
              type="button"
              onClick={() => (mode === 'create' ? onClose() : setEditing(false))}
            >
              <X size={14} aria-hidden /> Cancel
            </button>
            <button
              type="button"
              className="primary"
              onClick={onSave}
              disabled={persisting || !name}
            >
              <Check size={14} aria-hidden /> {mode === 'create' ? 'Create' : 'Save'}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="danger" onClick={onDelete} disabled={persisting}>
              <Trash2 size={14} aria-hidden /> Delete
            </button>
            {mode === 'detail' && client.data && (
              <button
                type="button"
                onClick={() => test.mutate(client.data!.id)}
                disabled={test.isPending}
              >
                <Zap size={14} aria-hidden /> {test.isPending ? 'Testing…' : 'Test'}
              </button>
            )}
            <button type="button" onClick={() => setEditing(true)}>
              <Edit2 size={14} aria-hidden /> Edit
            </button>
          </>
        )
      }
    >
      <div className="drawer-stack">
        {mode === 'detail' && client.isLoading && <p className="subtle">Loading…</p>}

        {test.data && (
          <div
            style={{
              border: '1px solid var(--line)',
              background: test.data.ok ? 'rgba(47, 133, 90, 0.06)' : 'rgba(192, 57, 43, 0.06)',
              borderRadius: 8,
              padding: 10,
              fontSize: 13,
            }}
          >
            <strong style={{ color: test.data.ok ? '#2f855a' : '#c0392b' }}>
              {test.data.ok ? '✓ Configuration looks good' : '✗ Configuration has issues'}
            </strong>
            {test.data.issues && test.data.issues.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {test.data.issues.map((iss) => (
                  <li key={iss}>{iss}</li>
                ))}
              </ul>
            )}
            {test.data.next_steps && test.data.next_steps.length > 0 && (
              <>
                <div style={{ marginTop: 6, fontWeight: 600 }}>Next steps:</div>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {test.data.next_steps.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        <HelpCallout
          title={mode === 'create' ? 'Registering an OAuth client' : 'About this OAuth client'}
          whatHappens={
            <>
              An OAuth client is the identity an MCP-using app (Claude Desktop, Cursor, an internal
              agent) presents to the gateway. Saving emits a <code>client.create</code> or{' '}
              <code>client.update</code> audit event.
            </>
          }
          youNeed={[
            <>
              The client <strong>type</strong> — <code>claude_desktop</code> /{' '}
              <code>claude_ai</code> are public PKCE; <code>s2s</code> uses{' '}
              <code>client_credentials</code>.
            </>,
            <>
              The owning workgroup. The gateway enforces that the client's principals are members of
              an effective WG that includes this owner.
            </>,
            <>
              <strong>Allowed virtual servers</strong> restricts which curated bundles this client
              can mount. Empty = unrestricted (any virtual server the principal can reach).
            </>,
          ]}
          afterwards={[
            <>
              For PKCE clients: feed the gateway URL (<code>/mcp</code>) into Claude Desktop; users
              sign in via Stanford SSO.
            </>,
            <>
              For S2S clients: rotate the secret on a schedule. Use <code>/oauth/token</code> with{' '}
              <code>client_credentials</code> to mint short-lived tokens.
            </>,
          ]}
        />

        <fieldset style={fieldsetStyle} disabled={!editing}>
          <legend style={legendStyle}>Identity</legend>
          {editing && <RequiredLegend />}
          {mode === 'detail' && client.data && (
            <Field label="Client ID">
              <code style={{ fontSize: 12 }}>{client.data.id}</code>
            </Field>
          )}
          <Field label="Type" required>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ClientType)}
              style={inputStyle}
              disabled={mode === 'detail'}
              required
            >
              {CLIENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Display name" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Owning WG" required>
            <select
              value={ownerWg}
              onChange={(e) => setOwnerWg(e.target.value)}
              style={inputStyle}
              disabled={mode === 'detail'}
              required
            >
              <option value="">—</option>
              {(wgs.data?.items ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.id} — {w.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'active' | 'retired')}
              style={inputStyle}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </fieldset>

        <fieldset style={fieldsetStyle} disabled={!editing}>
          <legend style={legendStyle}>Allowed scopes</legend>
          <ChipEditor
            values={scopes}
            onChange={setScopes}
            placeholder="add scope (e.g. mcp:invoke)"
            suggestions={SCOPE_SUGGESTIONS}
            editable={editing}
          />
        </fieldset>

        <fieldset style={fieldsetStyle} disabled={!editing}>
          <legend style={legendStyle}>Redirect URIs</legend>
          <ChipEditor
            values={redirects}
            onChange={setRedirects}
            placeholder="https://claude.ai/api/mcp/auth_callback"
            editable={editing}
          />
        </fieldset>

        <fieldset style={fieldsetStyle} disabled={!editing}>
          <legend style={legendStyle}>Allowed virtual-server slugs</legend>
          <p className="subtle" style={{ fontSize: 13, margin: '0 0 6px' }}>
            Empty = client may use the default <code>/mcp</code>. List slugs to scope to specific
            bundles.
          </p>
          <ChipEditor
            values={vsSlugs}
            onChange={setVsSlugs}
            placeholder="research-data"
            editable={editing}
          />
        </fieldset>

        {actionError && (
          <p style={{ color: '#c0392b', fontSize: 14 }} role="alert">
            <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {actionError}
          </p>
        )}

        {mode === 'detail' && !editing && (
          <p className="subtle" style={{ fontSize: 13 }}>
            <KeyRound size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            This is the registered shape; the actual client_secret is never stored — Stanford SSO
            issues short-lived JWTs against this row's allowlists.
          </p>
        )}
      </div>
    </Drawer>
  );
}

interface ChipEditorProps {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
  editable: boolean;
}

function ChipEditor({
  values,
  onChange,
  placeholder,
  suggestions,
  editable,
}: ChipEditorProps): JSX.Element {
  const [draft, setDraft] = useState('');
  const add = (): void => {
    const v = draft.trim();
    if (!v || values.includes(v)) {
      setDraft('');
      return;
    }
    onChange([...values, v]);
    setDraft('');
  };
  const remove = (v: string): void => onChange(values.filter((x) => x !== v));

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
        {values.length === 0 && (
          <span className="subtle" style={{ fontSize: 13 }}>
            (none)
          </span>
        )}
        {values.map((v) => (
          <span
            key={v}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 8px',
              borderRadius: 999,
              background: 'rgba(0,0,0,0.06)',
              fontSize: 13,
            }}
          >
            <code>{v}</code>
            {editable && (
              <button
                type="button"
                onClick={() => remove(v)}
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 0,
                  color: '#c0392b',
                }}
                aria-label={`Remove ${v}`}
              >
                <X size={12} />
              </button>
            )}
          </span>
        ))}
      </div>
      {editable && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            placeholder={placeholder}
            list={suggestions ? `chip-suggestions-${placeholder}` : undefined}
            style={{ ...inputStyle, flex: 1 }}
          />
          {suggestions && (
            <datalist id={`chip-suggestions-${placeholder}`}>
              {suggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          )}
          <button type="button" onClick={add} disabled={!draft.trim()}>
            Add
          </button>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, max-content) 1fr',
        gap: 12,
        alignItems: 'center',
        marginBottom: 8,
      }}
    >
      <span className="subtle" style={{ fontSize: 13 }}>
        {label}
        {required && <Required />}
      </span>
      <span style={{ fontSize: 14 }}>{children}</span>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--line)',
  borderRadius: 8,
  fontSize: 14,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};

const fieldsetStyle: React.CSSProperties = {
  border: '1px solid var(--line)',
  borderRadius: 8,
  padding: 12,
};

const legendStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  padding: '0 6px',
  color: 'var(--muted-foreground, #888)',
};
